import React, { useState, useContext } from "react";
import { useQuery, useMutation } from "@apollo/client";
import {
  Button,
  Header,
  Icon,
  Loader,
  Message,
  Accordion,
  AccordionTitle,
  AccordionContent,
  Label,
  Form,
  TextArea,
  Input,
  Confirm,
  Segment,
  List,
} from "semantic-ui-react";
import {
  GET_MY_ANNOUNCEMENTS,
  UPDATE_STUDENT_REPORT,
  SEND_STUDENT_REPORT,
  DELETE_STUDENT_REPORT,
} from "../GraphQL";
import UserContext from "contexts/UserContext";
import FileDropzone from "./FileDropzone";
import Attachment from "components/Attachment";
import moment from "moment";
import { v4 as uuidv4 } from "uuid";

// Strip Apollo's auto-injected __typename (and anything else not in FileInput)
const cleanFile = (f) => ({
  key: f.key,
  name: f.name,
  contentType: f.contentType,
  url: f.url,
});

const AnnouncementsList = () => {
  const user = useContext(UserContext);
  const [activeIndex, setActiveIndex] = useState(-1);
  const isPro = user.isPro();

  // Create form state
  const [isCreating, setIsCreating] = useState(false);
  const [newId, setNewId] = useState(null);
  const [newSubject, setNewSubject] = useState("");
  const [newMessage, setNewMessage] = useState("");
  const [newFiles, setNewFiles] = useState([]);

  // Edit state (map of announcement ID to edit values)
  const [editStates, setEditStates] = useState({});

  // Delete confirmation
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  // Send confirmation: { mode: "create" } or { mode: "update", announcement }
  const [sendConfirm, setSendConfirm] = useState(null);

  // Which specific button is currently submitting, e.g. "create-send",
  // "create-draft", `update-send:${id}`, `update-draft:${id}` — lets each
  // button show its own loading state instead of all of them lighting up.
  const [busyAction, setBusyAction] = useState(null);
  const isAnyLoading = busyAction !== null;

  const { loading, error, data, refetch } = useQuery(GET_MY_ANNOUNCEMENTS, {
    variables: { limit: 10 },
    skip: !isPro, // Don't query if not Pro
  });

  const [updateReport] = useMutation(UPDATE_STUDENT_REPORT);
  const [sendReport] = useMutation(SEND_STUDENT_REPORT);
  const [deleteReport] = useMutation(DELETE_STUDENT_REPORT);

  const handleClick = (e, titleProps) => {
    const { index } = titleProps;
    const newIndex = activeIndex === index ? -1 : index;
    setActiveIndex(newIndex);

    // Initialize edit state when opening a draft announcement
    if (newIndex !== -1 && announcements[index] && !announcements[index].sent) {
      const announcement = announcements[index];
      if (!editStates[announcement.id]) {
        setEditStates({
          ...editStates,
          [announcement.id]: {
            subject: getSubject(announcement),
            message: getMessage(announcement),
            files: announcement.files || [],
          },
        });
      }
    }
  };

  // Add/remove files for the create form
  const handleCreateDropComplete = (file) => {
    setNewFiles([...newFiles, file]);
  };

  const handleRemoveNewFile = (fileKey) => {
    setNewFiles(newFiles.filter((f) => f.key !== fileKey));
  };

  // Add/remove files for a draft's edit state
  const handleEditDropComplete = (announcementId, file) => {
    const editState = editStates[announcementId];
    updateEditField(announcementId, "files", [...(editState.files || []), file]);
  };

  const handleRemoveEditFile = (announcementId, fileKey) => {
    const editState = editStates[announcementId];
    updateEditField(
      announcementId,
      "files",
      (editState.files || []).filter((f) => f.key !== fileKey)
    );
  };

  // Extract subject and message from fields
  const getSubject = (announcement) => {
    const subjectField = announcement.fields.find((f) => f.label === "Subject");
    return subjectField?.value || "Untitled Announcement";
  };

  const getMessage = (announcement) => {
    const messageField = announcement.fields.find((f) => f.label === "Message");
    return messageField?.value || "";
  };

  // Create new announcement
  const handleCreate = async (sendImmediately = false) => {
    if (!newSubject.trim() || !newMessage.trim()) {
      alert("Please enter both a subject and message");
      return;
    }

    const teacherId = user.canonicalId;
    const newAnnouncementId = newId;

    setBusyAction(sendImmediately ? "create-send" : "create-draft");
    try {
      const input = {
        id: newAnnouncementId,
        reportType: "announcement",
        student: teacherId, // HACK: Store teacher ID in student field!
        date: new Date().toISOString(),
        sent: false,
        fields: [
          { label: "Subject", control: "Input", value: newSubject, options: [] },
          { label: "Message", control: "TextArea", value: newMessage, options: [] },
        ],
        files: newFiles.map(cleanFile),
      };

      console.log("[AnnouncementsList] Creating announcement with input:", input);
      const createResult = await updateReport({ variables: { input } });
      console.log("[AnnouncementsList] Create result:", createResult);

      // If sending immediately, send the report
      if (sendImmediately) {
        console.log("[AnnouncementsList] Sending announcement...", {
          student: teacherId,
          report: newAnnouncementId,
        });
        const sendResult = await sendReport({
          variables: {
            student: teacherId,
            report: newAnnouncementId,
          },
        });
        console.log("[AnnouncementsList] Send result:", sendResult);
      }

      // Reset form and refresh list
      setNewSubject("");
      setNewMessage("");
      setNewFiles([]);
      setNewId(null);
      setIsCreating(false);
      refetch();
    } catch (err) {
      console.error("[AnnouncementsList] Error creating announcement:", err);
      console.error("[AnnouncementsList] Error details:", {
        message: err.message,
        graphQLErrors: err.graphQLErrors,
        networkError: err.networkError,
      });
      alert("Failed to create announcement. Please check console for details.");
    } finally {
      setBusyAction(null);
    }
  };

  // Update existing announcement
  const handleUpdate = async (announcement, sendImmediately = false) => {
    const editState = editStates[announcement.id];
    if (!editState || !editState.subject.trim() || !editState.message.trim()) {
      alert("Please enter both a subject and message");
      return;
    }

    const teacherId = user.canonicalId;

    setBusyAction(sendImmediately ? `update-send:${announcement.id}` : `update-draft:${announcement.id}`);
    try {
      const input = {
        id: announcement.id,
        reportType: "announcement",
        student: teacherId,
        date: announcement.date,
        sent: announcement.sent,
        fields: [
          { label: "Subject", control: "Input", value: editState.subject, options: [] },
          { label: "Message", control: "TextArea", value: editState.message, options: [] },
        ],
        files: (editState.files || []).map(cleanFile),
      };

      console.log("[AnnouncementsList] Updating announcement with input:", input);
      const updateResult = await updateReport({ variables: { input } });
      console.log("[AnnouncementsList] Update result:", updateResult);

      // If sending immediately, send the report
      if (sendImmediately) {
        console.log("[AnnouncementsList] Sending announcement...", {
          student: teacherId,
          report: announcement.id,
        });
        const sendResult = await sendReport({
          variables: {
            student: teacherId,
            report: announcement.id,
          },
        });
        console.log("[AnnouncementsList] Send result:", sendResult);
      }

      // Refresh list and clear edit state
      refetch();
      setActiveIndex(-1);
    } catch (err) {
      console.error("[AnnouncementsList] Error updating announcement:", err);
      console.error("[AnnouncementsList] Error details:", {
        message: err.message,
        graphQLErrors: err.graphQLErrors,
        networkError: err.networkError,
      });
      alert("Failed to update announcement. Please check console for details.");
    } finally {
      setBusyAction(null);
    }
  };

  // Delete announcement
  const handleDelete = async (announcementId) => {
    setBusyAction(`delete:${announcementId}`);
    try {
      await deleteReport({ variables: { id: announcementId } });
      refetch();
      setActiveIndex(-1);
      setDeleteConfirm(null);
    } catch (err) {
      console.error("Error deleting announcement:", err);
      alert("Failed to delete announcement. Please try again.");
    } finally {
      setBusyAction(null);
    }
  };

  // Update edit state for a field
  const updateEditField = (announcementId, field, value) => {
    setEditStates({
      ...editStates,
      [announcementId]: {
        ...editStates[announcementId],
        [field]: value,
      },
    });
  };

  // Show upgrade message for non-Pro teachers
  if (!isPro) {
    return (
      <div style={{ marginBottom: "2em" }}>
        <Header as="h3" dividing>
          <Header.Content>Class Announcements</Header.Content>
        </Header>
        <Message info icon>
          <Icon name="star" />
          <Message.Content>
            <Message.Header>Upgrade to Pro for Class Announcements</Message.Header>
            <p>
              Send announcements to all parents at once! Communicate important
              updates, field trips, or class-wide information with a single message.
            </p>
            <Button primary as="a" href="/upgrade">
              Try Pro for free
            </Button>
          </Message.Content>
        </Message>
      </div>
    );
  }

  if (loading) {
    return <Loader active inline="centered" />;
  }

  if (error) {
    console.error("Error loading announcements:", error);
    return null;
  }

  const announcements = data?.myAnnouncements?.reports || [];

  return (
    <div style={{ marginBottom: "2em" }}>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          borderBottom: "1px solid rgba(34,36,38,.15)",
          paddingBottom: "0.5em",
          marginBottom: "1em",
        }}
      >
        <Header as="h3" style={{ margin: 0 }}>
          <Header.Content>Class Announcements</Header.Content>
        </Header>
        {!isCreating && (
          <Button
            basic
            onClick={() => {
              setNewId(uuidv4());
              setIsCreating(true);
            }}
          >
            <Button.Content>
              <Icon name="bullhorn" />
              <span className="mobile-hidden"> New Announcement</span>
            </Button.Content>
          </Button>
        )}
      </div>

      <div>
      {/* Inline Create Form */}
      {isCreating && (
        <Segment>
          <Form>
            <Form.Field>
              <label>Subject</label>
              <Input
                placeholder="Announcement subject..."
                value={newSubject}
                onChange={(e) => setNewSubject(e.target.value)}
                disabled={isAnyLoading}
              />
            </Form.Field>
            <Form.Field>
              <label>Message</label>
              <TextArea
                placeholder="Type your announcement message here..."
                value={newMessage}
                onChange={(e) => setNewMessage(e.target.value)}
                rows={5}
                disabled={isAnyLoading}
              />
            </Form.Field>
            <Form.Field>
              <label>
                <Icon name="attach" />
                Attachments
              </label>
              {newFiles.length > 0 ? (
                <List divided>
                  {newFiles.map((file) => (
                    <Attachment
                      key={file.key}
                      file={file}
                      showClose
                      onClose={() => handleRemoveNewFile(file.key)}
                    />
                  ))}
                </List>
              ) : null}
              <FileDropzone
                studentId={user.canonicalId}
                reportId={newId}
                onComplete={handleCreateDropComplete}
              />
            </Form.Field>
            <Button
              primary
              onClick={() => setSendConfirm({ mode: "create" })}
              disabled={isAnyLoading}
              loading={busyAction === "create-send"}
            >
              <Icon name="send" /> Create & Send
            </Button>
            <Button
              onClick={() => handleCreate(false)}
              disabled={isAnyLoading}
              loading={busyAction === "create-draft"}
            >
              <Icon name="save" /> Save Draft
            </Button>
            <Button
              basic
              onClick={() => {
                setIsCreating(false);
                setNewSubject("");
                setNewMessage("");
                setNewFiles([]);
                setNewId(null);
              }}
              disabled={isAnyLoading}
            >
              Cancel
            </Button>
          </Form>
        </Segment>
      )}

      {/* Announcements List */}
      {announcements.length === 0 && !isCreating ? (
        <Message info>
          <Message.Header>No announcements yet</Message.Header>
          <p>
            Create your first class announcement to communicate with all parents
            at once.
          </p>
        </Message>
      ) : (
        <Accordion styled fluid>
          {announcements.map((announcement, index) => {
            const isSent = announcement.sent;
            const editState = editStates[announcement.id];

            return (
              <React.Fragment key={announcement.id}>
                <AccordionTitle
                  active={activeIndex === index}
                  index={index}
                  onClick={handleClick}
                  style={{ display: "flex", alignItems: "center" }}
                >
                  <Icon name="dropdown" />
                  <strong>{getSubject(announcement)}</strong>
                  {isSent ? (
                    <Label size="tiny" color="green" style={{ marginLeft: "0.5em" }}>
                      <Icon name="check" /> Sent
                    </Label>
                  ) : (
                    <Label size="tiny" color="grey" style={{ marginLeft: "0.5em" }}>
                      <Icon name="edit" /> Draft
                    </Label>
                  )}
                  <span
                    style={{
                      marginLeft: "auto",
                      fontSize: "0.9em",
                      color: "#999",
                    }}
                  >
                    {moment(announcement.date).format("MMM D, YYYY")}
                  </span>
                </AccordionTitle>
                <AccordionContent active={activeIndex === index}>
                  {/* Draft: Show edit form */}
                  {!isSent && editState ? (
                    <Form>
                      <Form.Field>
                        <label>Subject</label>
                        <Input
                          value={editState.subject}
                          onChange={(e) =>
                            updateEditField(announcement.id, "subject", e.target.value)
                          }
                          disabled={isAnyLoading}
                        />
                      </Form.Field>
                      <Form.Field>
                        <label>Message</label>
                        <TextArea
                          value={editState.message}
                          onChange={(e) =>
                            updateEditField(announcement.id, "message", e.target.value)
                          }
                          rows={5}
                          disabled={isAnyLoading}
                        />
                      </Form.Field>
                      <Form.Field>
                        <label>
                          <Icon name="attach" />
                          Attachments
                        </label>
                        {editState.files && editState.files.length > 0 ? (
                          <List divided>
                            {editState.files.map((file) => (
                              <Attachment
                                key={file.key}
                                file={file}
                                showClose
                                onClose={() =>
                                  handleRemoveEditFile(announcement.id, file.key)
                                }
                              />
                            ))}
                          </List>
                        ) : null}
                        <FileDropzone
                          studentId={user.canonicalId}
                          reportId={announcement.id}
                          onComplete={(file) =>
                            handleEditDropComplete(announcement.id, file)
                          }
                        />
                      </Form.Field>
                      <Button
                        primary
                        onClick={() => setSendConfirm({ mode: "update", announcement })}
                        disabled={isAnyLoading}
                        loading={busyAction === `update-send:${announcement.id}`}
                      >
                        <Icon name="send" /> Update & Send
                      </Button>
                      <Button
                        onClick={() => handleUpdate(announcement, false)}
                        disabled={isAnyLoading}
                        loading={busyAction === `update-draft:${announcement.id}`}
                      >
                        <Icon name="save" /> Save Draft
                      </Button>
                      <Button
                        negative
                        basic
                        onClick={() => setDeleteConfirm(announcement.id)}
                        disabled={isAnyLoading}
                      >
                        <Icon name="trash" /> Delete
                      </Button>
                    </Form>
                  ) : (
                    /* Sent: Show read-only view */
                    <div>
                      <p style={{ whiteSpace: "pre-wrap", marginBottom: "1em" }}>
                        {getMessage(announcement)}
                      </p>
                      {announcement.files && announcement.files.length > 0 && (
                        <List divided style={{ marginBottom: "1em" }}>
                          {announcement.files.map((file) => (
                            <Attachment key={file.key} file={file} />
                          ))}
                        </List>
                      )}
                      <Button
                        negative
                        basic
                        size="small"
                        onClick={() => setDeleteConfirm(announcement.id)}
                        disabled={isAnyLoading}
                      >
                        <Icon name="trash" /> Delete
                      </Button>
                    </div>
                  )}
                </AccordionContent>
              </React.Fragment>
            );
          })}
        </Accordion>
      )}
      </div>

      {/* Delete Confirmation Dialog */}
      <Confirm
        open={deleteConfirm !== null}
        header="Delete Announcement"
        content="Are you sure you want to delete this announcement? This action cannot be undone."
        confirmButton={
          <Button negative loading={busyAction === `delete:${deleteConfirm}`}>
            Delete
          </Button>
        }
        onCancel={() => setDeleteConfirm(null)}
        onConfirm={() => handleDelete(deleteConfirm)}
      />

      {/* Send Confirmation Dialog */}
      <Confirm
        open={sendConfirm !== null}
        header="Send Announcement"
        content="Once an announcement is sent, it cannot be modified. Send the announcement?"
        confirmButton={
          <Button
            primary
            loading={
              sendConfirm?.mode === "create"
                ? busyAction === "create-send"
                : busyAction === `update-send:${sendConfirm?.announcement?.id}`
            }
          >
            Send
          </Button>
        }
        onCancel={() => setSendConfirm(null)}
        onConfirm={async () => {
          if (sendConfirm.mode === "create") {
            await handleCreate(true);
          } else {
            await handleUpdate(sendConfirm.announcement, true);
          }
          setSendConfirm(null);
        }}
      />
    </div>
  );
};

export default AnnouncementsList;
